import React, { useMemo, useState, useEffect } from "react";
import { Field, TextInput, TextArea } from "@/components/FormField";
import { Sparkles, X } from "lucide-react";

// English labels for common Devanagari / English placeholder tokens.
// Match is case-insensitive after stripping the brackets; first hit wins.
const LABEL_MAP = [
  // Marathi / Hindi
  [/खरेदीदार(ा|ी)?चे? नाव|खरेदी?दार/, "Purchaser name"],
  [/विक्रेत्या?चे? नाव|विक्रेता/, "Seller name"],
  [/अशिल(ा|ी)?चे? नाव|अशिल/, "Client name"],
  [/पत्ता|पता/, "Address"],
  [/मिळकत(ी)?चे? वर्णन|प्रॉपर्टी|मालमत्ता/, "Property description"],
  [/स[.\s]?नं|सर्व्हे|प्लॉट|सि[.\s]?टी[.\s]?एस|क्षेत्रफळ|सीमा/, "Property description"],
  [/मोबाईल|मोबाइल|फोन|दूरध्वनी/, "Mobile number"],
  [/तारीख|दिनांक/, "Date"],

  // English (preserve casing-friendly form)
  [/^purchaser/i, "Purchaser name"],
  [/^seller/i,    "Seller name"],
  [/^client/i,    "Client name"],
  [/^name$/i,     "Name"],
  [/address/i,    "Address"],
  [/property description|property$/i, "Property description"],
  [/survey|plot|cts|area|boundar/i,    "Property description"],
  [/dd\/?mm|date/i, "Date"],
  [/mobile|phone|contact/i, "Mobile number"],
];

const LONG_PATTERNS = /property description|address|description|details|boundaries|बोजा|वर्णन|पत्ता|सीमा|पता/i;

function tokenLabel(rawToken) {
  const inside = rawToken.replace(/^\[|\]$/g, "").trim();
  for (const [pat, label] of LABEL_MAP) {
    if (pat.test(inside)) return label;
  }
  // English ALL-CAPS or kebab fallback: Title-case the inner text.
  return inside
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .replace(/^./, (c) => c.toUpperCase());
}

// Tokens that are purely numeric (e.g. "[14]", "[१४]") are skipped — they're
// "in N days" defaults the advocate rarely needs to change.
const DEVANAGARI_DIGITS = "०१२३४५६७८९";
function isNumericToken(rawToken) {
  const inner = rawToken.replace(/^\[|\]$/g, "").trim();
  if (!inner) return true;
  return [...inner].every((ch) => /[0-9]/.test(ch) || DEVANAGARI_DIGITS.includes(ch));
}

function extractTokens(raw) {
  if (!raw) return [];
  const matches = raw.match(/\[[^\]\n]+?\]/g) || [];
  const uniq = Array.from(new Set(matches));
  return uniq.filter((t) => !isNumericToken(t));
}

export default function PlaceholderEditor({ templateRaw, onBodyChange, onClear }) {
  const tokens = useMemo(() => extractTokens(templateRaw), [templateRaw]);
  const [values, setValues] = useState({});

  useEffect(() => { setValues({}); }, [templateRaw]);

  const update = (tok, v) => {
    const next = { ...values, [tok]: v };
    setValues(next);
    let body = templateRaw;
    // Replace every token (including numeric ones we hid) once values are filled.
    const allTokens = Array.from(new Set((templateRaw || "").match(/\[[^\]\n]+?\]/g) || []));
    for (const t of allTokens) {
      const val = (next[t] || "").trim();
      if (val) {
        const safe = t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        body = body.replace(new RegExp(safe, "g"), val);
      }
    }
    onBodyChange(body);
  };

  if (tokens.length === 0) return null;

  const filledCount = tokens.filter((t) => (values[t] || "").trim().length > 0).length;

  return (
    <div className="bg-white border border-black/15 rounded-sm p-4 sm:p-5 space-y-4" data-testid="placeholder-editor">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5">
          <Sparkles size={16} className="text-black mt-0.5 shrink-0"/>
          <div>
            <div className="text-[10px] tracking-overline uppercase text-gray-500 font-semibold">Fill placeholders</div>
            <h3 className="font-display text-base sm:text-lg text-black font-bold leading-tight">
              {filledCount}/{tokens.length} filled — values flow straight into the ad below.
            </h3>
          </div>
        </div>
        <button
          type="button"
          onClick={onClear}
          className="text-xs text-gray-500 hover:text-red-500 flex items-center gap-1 shrink-0"
          data-testid="placeholder-editor-clear"
          title="Edit the ad text freehand instead"
        >
          <X size={12}/> Type freehand
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {tokens.map((tok) => {
          const label = tokenLabel(tok);
          const long = LONG_PATTERNS.test(tok) || LONG_PATTERNS.test(label);
          const slug = label.toLowerCase().replace(/[^a-z0-9]+/gi, "-");
          return (
            <div key={tok} className={long ? "sm:col-span-2" : ""}>
              <Field label={label} testid={`ph-${slug}`}>
                {long ? (
                  <TextArea
                    rows={3}
                    value={values[tok] || ""}
                    onChange={(v) => update(tok, v)}
                    placeholder={`Enter ${label.toLowerCase()}`}
                    testid={`ph-input-${slug}`}
                  />
                ) : (
                  <TextInput
                    value={values[tok] || ""}
                    onChange={(v) => update(tok, v)}
                    placeholder={`Enter ${label.toLowerCase()}`}
                    testid={`ph-input-${slug}`}
                  />
                )}
              </Field>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-gray-500">
        Tip: leave a field empty to keep its <span className="font-mono">[placeholder]</span> visible in the ad. The textarea below shows the live result.
      </p>
    </div>
  );
}
